/**
 * Icon set.
 *
 * Replaces the emoji the app shipped with. Emoji render differently on every
 * platform and read as playful; these are drawn from Christian visual
 * tradition — cross, dove, ichthys, flame, arch, ribbon — on one 24px grid so
 * they sit together as a family.
 *
 * Geometric marks are stroked (1.6px, round joins) to match the UI's weight;
 * the two organic marks, dove and ichthys, are filled, because thin strokes
 * turn to mush at 18px.
 *
 * Every icon is decorative: callers keep a real text label, and the <svg> is
 * aria-hidden.
 */

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const PATHS = {
  /** Latin cross — navigation home, and the app mark. */
  cross: (
    <g {...STROKE}>
      <path d="M12 3.2v17.6" />
      <path d="M7.6 8.6h8.8" />
    </g>
  ),

  /** Open book — the verse deck. */
  book: (
    <g {...STROKE}>
      <path d="M12 7.4C10.1 5.9 7.6 5.3 4.5 5.5v11.7c3.1-.2 5.6.4 7.5 1.9 1.9-1.5 4.4-2.1 7.5-1.9V5.5c-3.1-.2-5.6.4-7.5 1.9Z" />
      <path d="M12 7.4v11.7" />
    </g>
  ),

  /** Flame of the Spirit — the quiz, and milestone rewards. */
  flame: (
    <g {...STROKE}>
      {/* Asymmetric, with a licking tip — a symmetrical rounded top reads as
          a water droplet, which is the wrong sacrament entirely. */}
      <path d="M13.4 2.4c-.3 2.8-1.6 4.3-3.1 5.8-1.8 1.8-3.6 3.6-3.6 6.6a5.5 5.5 0 0 0 11 0c0-2.1-.9-3.7-2-5.1-.3 1-.9 1.7-1.8 2.1.7-3.6-.1-6.8-.5-9.4Z" />
      <path d="M12 20.9a2.7 2.7 0 0 0 2.7-2.7c0-1.5-1.2-2.5-1.9-3.9-1 1.3-3.5 2.3-3.5 3.9a2.7 2.7 0 0 0 2.7 2.7Z" />
    </g>
  ),

  /** Cathedral arch — the book-by-book learning paths. */
  arch: (
    <g {...STROKE}>
      <path d="M4.8 20.6V11a7.2 7.2 0 0 1 14.4 0v9.6" />
      <path d="M12 20.6v-7.4" />
      <path d="M3.6 20.6h16.8" />
    </g>
  ),

  /** Bible ribbon marker — bookmarks. */
  ribbon: (
    <g {...STROKE}>
      <path d="M6.6 3.6h10.8v16.8L12 16.3l-5.4 4.1V3.6Z" />
    </g>
  ),

  /**
   * Descending dove — the Scripture AI companion.
   *
   * Front-facing with spread wings rather than in profile: the profile
   * silhouette collapses into a leaf shape at 18px, while the symmetrical
   * spread-wing form stays unmistakable.
   */
  dove: (
    <g fill="currentColor">
      {/* Head overlaps the body by ~0.3px so the two shapes stay one solid
          silhouette at 18px instead of separating into a dot and a leaf. */}
      <circle cx="12" cy="5.4" r="2.1" />
      <path d="M12 7.1c-1.2 2-1.8 4.4-1.8 7 0 2.2.5 4.3 1.4 6.3.2.5.6.5.8 0 .9-2 1.4-4.1 1.4-6.3 0-2.6-.6-5-1.8-7Z" />
      <path d="M10.2 9.8C7.6 7.3 4.5 6.3 1 6.9c1.4 3.8 4.2 6.1 8.4 7 .2-1.4.5-2.7.8-4.1Z" />
      <path d="M13.8 9.8c2.6-2.5 5.7-3.5 9.2-2.9-1.4 3.8-4.2 6.1-8.4 7-.2-1.4-.5-2.7-.8-4.1Z" />
    </g>
  ),

  /** Ichthys — verses learned. */
  fish: (
    <path
      fill="currentColor"
      d="M21.4 11.6c-2.9-3.3-7.3-5.1-11.6-5.1-1.9 0-3.7.3-5.4 1-.4.2-.5.7-.2 1l2.5 3.5-2.5 3.5c-.3.3-.2.8.2 1 1.7.7 3.5 1 5.4 1 4.3 0 8.7-1.8 11.6-5.1.2-.2.2-.6 0-.8ZM16 11.2a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8Z"
    />
  ),

  /** Circling arrows — review mode. */
  renew: (
    <g {...STROKE}>
      <path d="M20.2 12a8.2 8.2 0 0 1-13.9 6" />
      <path d="M3.8 12a8.2 8.2 0 0 1 13.9-6" />
      <path d="M17.9 2.6v3.6h-3.6" />
      <path d="M6.1 21.4v-3.6h3.6" />
    </g>
  ),

  /**
   * Radiant cross — level-ups and premium.
   *
   * The cross is drawn heavier and the rays kept short and diagonal, clear of
   * the arms; rays at the cardinal points turn the whole mark into a sparkle
   * and the cross stops reading.
   */
  radiance: (
    <g {...STROKE}>
      <path strokeWidth="1.9" d="M12 4.6v14.8" />
      <path strokeWidth="1.9" d="M8.3 9.2h7.4" />
      <g opacity="0.55">
        <path d="M4.9 5.2l1.5 1.5M19.1 5.2l-1.5 1.5M4.9 17.4l1.5-1.5M19.1 17.4l-1.5-1.5" />
      </g>
    </g>
  ),

  /** Tablets of the law — empty states and quiz results. */
  tablets: (
    <g {...STROKE}>
      <path d="M4.4 10a3.3 3.3 0 0 1 6.6 0v9.8H4.4V10Z" />
      <path d="M13 10a3.3 3.3 0 0 1 6.6 0v9.8H13V10Z" />
      <path d="M6.6 13.4h2.2M15.2 13.4h2.2M6.6 16.4h2.2M15.2 16.4h2.2" opacity="0.55" />
    </g>
  ),

  /**
   * Scroll — the story mode.
   *
   * Rolled at both ends rather than one: a single roll reads as a rolling pin,
   * and the two curls are what separate a scroll from a plain sheet at 18px.
   */
  scroll: (
    <g {...STROKE}>
      <path d="M6.6 4.6h12.2a2.2 2.2 0 0 1 0 4.4H6.6" />
      <path d="M17.4 19.4H5.2a2.2 2.2 0 0 1 0-4.4h12.2" />
      <path d="M6.6 4.6a2.2 2.2 0 0 0 0 4.4v6" />
      <path d="M17.4 19.4a2.2 2.2 0 0 0 0-4.4V9" />
      <path d="M9.6 11.8h5.2" opacity="0.55" />
    </g>
  ),

  lock: (
    <g {...STROKE}>
      <rect x="4.8" y="10.4" width="14.4" height="9.6" rx="2.2" />
      <path d="M8.2 10.4V7.8a3.8 3.8 0 0 1 7.6 0v2.6" />
    </g>
  ),

  check: (
    <g {...STROKE}>
      <path d="M4.8 12.6l4.8 4.8L19.2 6.6" />
    </g>
  ),

  soundOn: (
    <g {...STROKE}>
      <path d="M11 5 6 9H2v6h4l5 4V5Z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M18.5 5.5a9 9 0 0 1 0 13" />
    </g>
  ),

  soundOff: (
    <g {...STROKE}>
      <path d="M11 5 6 9H2v6h4l5 4V5Z" />
      <path d="m22 9-6 6" />
      <path d="m16 9 6 6" />
    </g>
  ),

  /** Filled, not stroked — a stroked triangle reads as an outline arrow. */
  play: (
    <path fill="currentColor" d="M8 5.4a.9.9 0 0 1 1.37-.77l9.1 6.6a.9.9 0 0 1 0 1.54l-9.1 6.6A.9.9 0 0 1 8 18.6V5.4Z" />
  ),

  pause: (
    <g fill="currentColor">
      <rect x="7" y="5" width="3.6" height="14" rx="1.3" />
      <rect x="13.4" y="5" width="3.6" height="14" rx="1.3" />
    </g>
  ),

  /** Clock — the timed practice mode. */
  clock: (
    <g {...STROKE}>
      <circle cx="12" cy="12.8" r="8.2" />
      <path d="M12 8.4v4.4l3 1.8" />
      <path d="M9.4 2.8h5.2" />
    </g>
  ),

  /** Concentric rings — the weak-spots mode. */
  target: (
    <g {...STROKE}>
      <circle cx="12" cy="12" r="8.4" />
      <circle cx="12" cy="12" r="4.6" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" />
    </g>
  ),

  /** Head and shoulders — the characters section. */
  people: (
    <g {...STROKE}>
      <circle cx="12" cy="8.2" r="3.9" />
      <path d="M4.8 20.4a7.2 7.2 0 0 1 14.4 0" />
    </g>
  ),

  /** Five-pointed star — chapter ratings. Filled, so it reads at 17px. */
  star: (
    <path
      fill="currentColor"
      d="M12 2.9a.7.7 0 0 1 .63.4l2.42 4.9 5.41.79a.7.7 0 0 1 .39 1.2l-3.92 3.81.93 5.39a.7.7 0 0 1-1.02.74L12 17.58l-4.84 2.55a.7.7 0 0 1-1.02-.74l.93-5.39-3.92-3.82a.7.7 0 0 1 .39-1.19l5.41-.79 2.42-4.9a.7.7 0 0 1 .63-.4Z"
    />
  ),

  /** Skip forward — next passage. */
  skip: (
    <g fill="currentColor">
      <path d="M6.6 5.6a.85.85 0 0 1 1.3-.72l8 6.4a.85.85 0 0 1 0 1.44l-8 6.4a.85.85 0 0 1-1.3-.72V5.6Z" />
      <rect x="16.4" y="5" width="2.6" height="14" rx="1.2" />
    </g>
  ),

  /** Headphones — the listening dock. */
  headphones: (
    <g {...STROKE}>
      <path d="M4.4 15.4v-3a7.6 7.6 0 0 1 15.2 0v3" />
      <path d="M4.4 14.2h1.9a1.6 1.6 0 0 1 1.6 1.6v2.4a1.6 1.6 0 0 1-1.6 1.6H6a1.6 1.6 0 0 1-1.6-1.6v-4Z" />
      <path d="M19.6 14.2h-1.9a1.6 1.6 0 0 0-1.6 1.6v2.4a1.6 1.6 0 0 0 1.6 1.6h.3a1.6 1.6 0 0 0 1.6-1.6v-4Z" />
    </g>
  ),

  /** Soundwave bars — the music bed toggle. */
  // A winding route, for the trail view.
  path: (
    <g {...STROKE}>
      <path d="M6 20c0-4 12-4 12-8s-8-4-8-8" />
      <circle cx="6" cy="20" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="10" cy="4" r="1.6" fill="currentColor" stroke="none" />
    </g>
  ),

  // A globe with meridians, for the map view.
  globe: (
    <g {...STROKE}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M3.4 12h17.2" />
      <ellipse cx="12" cy="12" rx="4" ry="8.6" />
    </g>
  ),

  // An outbound arrow, for sharing.
  share: (
    <g {...STROKE}>
      <path d="M12 15.5V4m0 0L8.4 7.6M12 4l3.6 3.6" />
      <path d="M5.5 13.5v5a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5v-5" />
    </g>
  ),

  waves: (
    <g {...STROKE}>
      <path d="M3.4 10.6v2.8M7.4 7.6v8.8M11.4 4.8v14.4M15.4 8.4v7.2M19.4 11v2" />
    </g>
  ),

  close: (
    <g {...STROKE}>
      <path d="M6 6l12 12M18 6L6 18" />
    </g>
  ),

  arrow: (
    <g {...STROKE}>
      <path d="M4.5 12h15" />
      <path d="M13.5 6l6 6-6 6" />
    </g>
  ),

  /**
   * Four corner brackets, opening outwards — go fullscreen.
   *
   * Corners rather than the more common four diagonal arrows: at 18px the
   * arrowheads close up into blobs, while a bare right-angle stays a crisp
   * right-angle, and the empty middle is what says "the frame gets bigger".
   */
  expand: (
    <g {...STROKE}>
      <path d="M9.4 4.4H4.4v5" />
      <path d="M14.6 4.4h5v5" />
      <path d="M19.6 14.6v5h-5" />
      <path d="M9.4 19.6h-5v-5" />
    </g>
  ),

  /**
   * The same four corners turned inwards — leave fullscreen.
   *
   * Pushed a little further apart than a mirror of `expand` would put them: the
   * inward corners point at each other, and at 17px a gap of much less than
   * this closes up and the mark starts reading as a plus sign.
   */
  collapse: (
    <g {...STROKE}>
      <path d="M4 8.8h4.8V4" />
      <path d="M20 8.8h-4.8V4" />
      <path d="M15.2 20v-4.8H20" />
      <path d="M8.8 20v-4.8H4" />
    </g>
  ),
};

export default function Icon({ name, size = 20, className = '' }) {
  const path = PATHS[name];
  if (!path) return null;

  return (
    <svg
      className={`icon${className ? ` ${className}` : ''}`}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );
}

export const ICON_NAMES = Object.keys(PATHS);
